import {
  Chip,
  createTheme,
  Loader,
  LoadingOverlay,
  MantineProvider,
} from '@mantine/core';
import React, { ReactNode } from 'react';

import useResponsive from '../hooks/useResponsive';

type Props = {
  children: ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const { isMobile, isTablet } = useResponsive();

  const hfwColors: string[] = [
    '#ebf2ff',
    '#94b5e8',
    '#647fcc',
    '#4055a6',
    '#1c2a8b',
    '#171e96',
    '#10069f',
    '#100691',
    '#0e057a',
    '#0b055e',
  ];

  const theme = createTheme({
    cursorType: 'pointer',
    fontFamily:
      'Manrope, apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen-Sans, Ubuntu, Cantarell, Helvetica Neue, sans-serif',
    headings: {
      fontFamily:
        'Manrope, apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen-Sans, Ubuntu, Cantarell, Helvetica Neue, sans-serif',
    },
    colors: {
      // @ts-ignore
      hifivework: hfwColors,
    },
    spacing: {
      xs: isMobile ? '0.25625rem' : isTablet ? '0.25rem' : '0.2875rem', // 0.25rem * 1.15
      sm: isMobile ? '0.5125rem' : isTablet ? '0.5rem' : '0.575rem',
      md: isMobile ? '1.025rem' : isTablet ? '1rem' : '1.15rem',
      lg: isMobile ? '1.5375rem' : isTablet ? '1.5rem' : '1.725rem',
      xl: isMobile ? '2.05rem' : isTablet ? '2rem' : '2.3rem',
    },
    fontSizes: {
      xs: isMobile ? '0.6875rem' : isTablet ? '0.7rem' : '0.75rem',
      sm: isMobile ? '0.80625rem' : isTablet ? '0.85rem' : '0.875rem',
      md: isMobile ? '0.91875rem' : isTablet ? '0.95rem' : '1rem',
      lg: isMobile ? '1.03125rem' : isTablet ? '1.075rem' : '1.125rem',
      xl: isMobile ? '1.14375rem' : isTablet ? '1.175rem' : '1.25rem',
    },
    primaryColor: 'hifivework',
    components: {
      Button: {
        defaultProps: {
          radius: 'md',
        },
      },
      Chip: Chip.extend({
        defaultProps: {
          variant: 'filled',
        },
      }),
      ColorInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      Dropzone: {
        styles: () => ({
          root: {
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }),
      },
      Fieldset: {
        styles: () => ({
          legend: {
            fontWeight: 'bold',
          },
        }),
      },
      Loader: Loader.extend({
        defaultProps: {
          type: 'bars',
        },
      }),
      LoadingOverlay: LoadingOverlay.extend({
        defaultProps: {
          overlayProps: {
            blur: 2,
          },
          loaderProps: {
            type: 'bars',
          },
        },
      }),
      Modal: {
        styles: () => ({
          close: {
            color: '#707070',
          },
        }),
      },
      Badge: {
        styles: () => ({
          inner: {
            textTransform: 'none',
            fontWeight: 'normal',
          },
        }),
      },
      RadioGroup: {
        styles: () => ({
          label: {
            marginBottom: 10,
          },
          description: {
            color: 'black',
            marginBottom: 10,
          },
        }),
      },
      Tabs: {
        styles: () => ({
          tab: {
            fontWeight: 'bold',
          },
        }),
        defaultProps: {
          variant: 'outline',
          radius: 'md',
        },
      },
      TextInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      FileInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      DatePickerInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      DateTimePicker: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      NumberInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      PasswordInput: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      Select: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
      MultiSelect: {
        styles: () => ({
          label: {
            display: 'flex',
            gap: '4px',
          },
        }),
      },
    },
  });

  return (
    <MantineProvider defaultColorScheme="light" theme={theme}>
      {children}
    </MantineProvider>
  );
}
